import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@/../environments/environment';

export interface CreateEvaluatorRequest {
  cpf: string;
  nomeCompleto: string;
  email: string;
  telefone: string;
  senha: string;
}

export interface UpdateEvaluatorRequest {
  cpf?: string;
  nomeCompleto?: string;
  email?: string;
  telefone?: string;
  senha?: string;
  ativo?: boolean;
}

export interface Evaluator {
  id: number;
  cpf: string;
  nomeCompleto: string;
  email: string;
  telefone: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EvaluatorListResponse {
  content: Evaluator[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface DesignarEditaisRequest {
  editalIds: number[];
}

export interface EditalVinculado {
  id: number;
  numeroEdital: string;
  nome: string;
  status: string;
}

export interface EvaluatorComEditais extends Evaluator {
  editais: EditalVinculado[];
}

@Injectable({
  providedIn: 'root',
})
export class EvaluatorService {
  private apiUrl = `${environment.apiUrl}/admin/avaliadores`;

  constructor(private http: HttpClient) {}

  /**
   * Get all evaluators with pagination and optional search
   */
  getEvaluators(params?: {
    name?: string;
    page?: number;
    size?: number;
  }): Observable<EvaluatorListResponse> {
    let httpParams = new HttpParams();

    if (params?.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params?.name) {
      httpParams = httpParams.set('name', params.name);
    }

    return this.http.get<EvaluatorListResponse>(this.apiUrl, {
      params: httpParams,
    });
  }

  /**
   * Get evaluator by ID
   */
  getEvaluatorById(id: number): Observable<Evaluator> {
    return this.http.get<Evaluator>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create new evaluator
   */
  createEvaluator(data: CreateEvaluatorRequest): Observable<Evaluator> {
    return this.http.post<Evaluator>(this.apiUrl, data);
  }

  /**
   * Update evaluator
   */
  updateEvaluator(
    id: number,
    data: UpdateEvaluatorRequest
  ): Observable<Evaluator> {
    return this.http.put<Evaluator>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete evaluator
   */
  deleteEvaluator(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Toggle evaluator active status
   */
  toggleEvaluatorStatus(id: number, active: boolean): Observable<Evaluator> {
    return this.http.patch<Evaluator>(`${this.apiUrl}/${id}/toggle-status`, { active: active });
  }

  /**
   * Designa editais ao avaliador
   * PUT /api/admin/avaliadores/{id}/editais
   */
  designarEditais(id: number, editalIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/editais`, { editalIds });
  }

  /**
   * Busca editais vinculados ao avaliador
   * GET /api/admin/avaliadores/{id}/editais
   */
  getEditaisVinculados(id: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiUrl}/${id}/editais`);
  }

  /**
   * Desvincula editais do avaliador
   * DELETE /api/admin/avaliadores/{id}/editais
   */
  desvincularEditais(id: number, editalIds: number[]): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/editais`, {
      body: { editalIds }
    });
  }

  /**
   * Lista avaliadores de um edital
   * GET /api/admin/avaliadores/editais/{editalId}
   */
  getAvaliadoresPorEdital(editalId: number): Observable<EvaluatorComEditais[]> {
    return this.http.get<EvaluatorComEditais[]>(`${this.apiUrl}/editais/${editalId}`);
  }
}
